import React, {
  forwardRef,
  JSX,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@emotion/react';
import { useClickAway, useMount, useWindowSize } from 'react-use';
import { decodeObjectBase64, LoggerFactory } from '@ibe/services';
import useConfig from '../../../Hooks/useConfig';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/date-range-input.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-input.en.t.json.keys';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import {
  ApiGetPackagesRequest,
  ApiGetPackagesRequestFromJSON,
  ApiItemType,
  ApiPackageModel
} from '@ibe/api';
import DateUtil from '../../../../MMB/Components/Util/DateUtil';
import ServiceKey from '../../../../Models/ServiceKey';
import useQuery from '../../../../MMB/Hooks/useQuery';
import useApi from '../../../Hooks/useApi';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DatePickerComponent } from '../DatePicker/withAdvancedFeatures';
import DateRangeDisplay from './DateRangeDisplay';
import {
  DateRangeInputProps,
  INVALID_DATE_STRING,
  ITEM,
  OCCUPANCY,
  PACKAGE_CODE,
  SALES_CHANNEL_CODE,
  WeekDayConstraints
} from './DateRangeInputProps';
import DateRangeInputPopover from './DateRangeInputPopover';
import renderDayContents from '../DatePicker/RenderDayContents';
import { NUMBER_DAY_PER_WEEK } from '../DatePicker/ReactDatePickerRenderer';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
const logger = LoggerFactory.get('DateRangeInput.tsx');

export interface HighlightDatesInformation {
  dateRanges: ({ startDate: string; endDate: string } | null)[];
  highlightIcon: JSX.Element;
}

const DateRangeInput = forwardRef(function DateRangeInput(
  props: DateRangeInputProps & DataTestIdProp,
  ref: Ref<HTMLDivElement>
): JSX.Element {
  const { t } = useTranslation('date-range-input', fallback, Keys);

  const config = useConfig();
  const theme = useTheme();

  const {
    customDateFormat,
    maxDateRangeSpan,
    id,
    minDate,
    maxDate,
    onChange,
    startDate,
    endDate,
    dataTestId,
    seasons,
    noDatePickerOverflow,
    updateStateFromProps,
    disabled,
    weekDayConstraints,
    datePickerRef,
    ignoreStartDateForFilter,
    selectsDisabledDaysInRange,
    focusSelectedMonth,
    useAdvancedFeatures,
    monthsShown,
    customDivider,
    CustomInput,
    CustomHeader,
    onCalendarClose,
    onCalendarCloseDated,
    popoverRef: externalPopoverRef,
    popoverTargetRef,
    dateRangeInputRef,
    onSubmit,
    stateFromPropsOnPopoverOpen,
    useLocalSelectedDatesForInRangeCheck,
    onMonthChange,
    onYearChange,
    onCalendarOpen,
    onPopoverToggle,
    fillWithLastDateRangeWhenNoSelection,
    clearSelectionsOnOpen,
    withColorCodedAvailability,
    selectedRoomServiceUnitTypeCode,
    showDateSelectionInModal,
    modalLabel,
    highlightDatesInformation,
    searchParams,
    selectsRange = true,
    showPopperArrow,
    withHeader,
    minMonthShown,
    popperPlacement
  } = props;
  const configData = useConfig();
  const beginningDate = startDate ? dayjs(startDate).toDate() : undefined;
  const endingDate = endDate ? dayjs(endDate).toDate() : undefined;
  const [startingDate, setStartDate] = useState<Date | undefined>(beginningDate);
  const [finalDate, setEndDate] = useState<Date | undefined>(endingDate);
  const [previousStartingDate, setPreviousStartDate] = useState<Date | undefined>(beginningDate);
  const [previousFinalDate, setPreviousEndDate] = useState<Date | undefined>(endingDate);
  const [seasonsEndDates, setSeasonsEndDates] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const initialRender = useRef<boolean>(false);
  const [closeOnSelect, setCloseOnSelect] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | undefined>(props.duration?.value?.duration);
  const { usePlaceholderAsOption, withDuration } = props.duration || {};
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [availabilityPackage, setAvailabilityPackage] = useState<ApiPackageModel>();
  const query = useQuery();
  const api = useApi();

  useImperativeHandle(
    dateRangeInputRef,
    () => ({
      togglePopover,
      openPopover: (): void => setPopoverOpen(true),
      closePopover: () => {
        setPopoverOpen(false);
        return [
          !!startingDate ? dateToString(startingDate) : undefined,
          !!finalDate ? dateToString(finalDate) : undefined
        ];
      },
      getCurrentDateRange: () => [
        !!startingDate ? dateToString(startingDate) : undefined,
        !!finalDate ? dateToString(finalDate) : undefined
      ]
    }),
    [startingDate, finalDate]
  );

  useMount(() => {
    initialRender.current = true;
  });

  useEffect(() => {
    if (seasons) {
      const seasonsEnds: string[] = [];
      seasons.forEach(season => {
        if (season.endDate) {
          seasonsEnds.push(season.endDate);
        }
      });
      const seasonsEndsFiltered = seasonsEnds.filter(end => {
        let isIncludedInSeason = false;
        seasons.forEach(season => {
          if (
            dayjs(end).isSame(season.startDate) ||
            (dayjs(end).isAfter(season.startDate) && dayjs(end).isBefore(season.endDate))
          ) {
            isIncludedInSeason = true;
          }
        });
        return !isIncludedInSeason;
      });
      setSeasonsEndDates(seasonsEndsFiltered);
    }
  }, [seasons]);

  useEffect(() => {
    if (initialRender.current) {
      if (withColorCodedAvailability && stateFromPropsOnPopoverOpen && popoverOpen) {
        const includedDatesFromSeasons = getIncludedDatesFromSeasons();
        let date = dayjs();

        if (!startDate && includedDatesFromSeasons) {
          for (const inclDate of includedDatesFromSeasons) {
            if (dayjs(inclDate).isSameOrAfter(date)) {
              date = dayjs(inclDate);
              break;
            }
          }
        }

        updatePackageAvailability(
          !!startDate && startDate !== INVALID_DATE_STRING
            ? dayjs(startDate).date(1).toDate()
            : date.date(1).toDate()
        );
      }

      setStartDate(
        !!startDate && startDate !== INVALID_DATE_STRING ? dayjs(startDate).toDate() : undefined
      );
      setEndDate(
        !!endDate && endDate !== INVALID_DATE_STRING ? dayjs(endDate).toDate() : undefined
      );
    }
  }, [stateFromPropsOnPopoverOpen, startDate, endDate, initialRender, popoverOpen]);

  useClickAway(externalPopoverRef || popoverRef, () => {
    setPopoverOpen(false);
    if (!!onCalendarClose) {
      onCalendarClose();
    }
    if (!!onCalendarCloseDated && startingDate && finalDate) {
      onCalendarCloseDated(dateToString(startingDate), dateToString(finalDate));
    }
  });

  const onChanging =
    onChange ||
    ((): void => {
      logger.log('change called');
    });

  const submit = useCallback((): void => {
    if (!!onSubmit) {
      onSubmit(
        !!startingDate ? dateToString(startingDate) : undefined,
        !!finalDate ? dateToString(finalDate) : undefined
      );
    }
  }, [onSubmit, startingDate, finalDate]);

  const onDurationChange =
    props.duration?.onDurationChange ||
    ((): void => {
      logger.log('duration change called');
    });

  const minDateRangeSpan =
    withDuration && duration && duration > (props.minDateRangeSpan || 0)
      ? duration
      : props.minDateRangeSpan;

  useEffect(() => {
    if (updateStateFromProps && !isCalendarOpen) {
      setStartDate(
        !!startDate && startDate !== INVALID_DATE_STRING ? new Date(startDate) : undefined
      );
      setEndDate(!!endDate && endDate !== INVALID_DATE_STRING ? new Date(endDate) : undefined);
      setDuration(props.duration?.value?.duration);
    }
  }, [startDate, endDate, props?.duration]);

  const dateFormat = configData.formatDate;

  const normalizedDateFormatForComponent = dateFormat
    .replace(/Y/g, 'y')
    .replace(/Y/g, 'y')
    .replace(/D/g, 'd')
    .replace(/m/g, 'M');
  const normalizedDateFormatForDayjs = dateFormat.toUpperCase();

  function getIncludedDatesFromSeasons(): Date[] | undefined {
    let includedDates: Date[] = [];

    if (!seasons || seasons.length <= 0) {
      return undefined;
    }

    seasons.forEach(season => {
      if (season.startDate && season.endDate) {
        const includedDatesInSeason = DateUtil.generateDatesInSpan(
          season.startDate,
          season.endDate,
          !startingDate || (!!startingDate && !!finalDate && !isNaN(finalDate.getTime()))
        );
        includedDates = includedDates.concat(includedDatesInSeason);
      }
    });
    return includedDates;
  }

  const getExcludedDates = useCallback((): Date[] => {
    return applyMinRange();
  }, [startingDate]);

  const dateToString = (dateObj: Date): string => {
    return dayjs(dateObj).format(normalizedDateFormatForDayjs);
  };

  const durationToString = () => {
    if (!duration) return '';
    const rest = duration % NUMBER_DAY_PER_WEEK;
    const numberOfWeeks = duration / NUMBER_DAY_PER_WEEK;
    if (rest || !numberOfWeeks)
      return t(Keys.nightComma, { count: duration, numberOfNights: duration });
    return t(Keys.weekComma, { count: numberOfWeeks, numberOfWeeks });
  };

  const handleChange = (dates: [Date, Date] | Date): void => {
    const [start, end] = Array.isArray(dates) ? dates : [dates, dates];
    setStartDate(start);
    setEndDate(end);
    onChanging(dateToString(start), dateToString(end));
    setCloseOnSelect(!end);
  };

  const handleDurationChange = useCallback(
    (numberOfNights?: number) => {
      if (finalDate && startingDate && numberOfNights) {
        const diff = dayjs(finalDate).diff(dayjs(startingDate), 'day');
        if (numberOfNights > diff) {
          const updatedDate = dayjs(startingDate).add(numberOfNights, 'day').format(dateFormat);
          setEndDate(new Date(updatedDate));
          onChanging(dateToString(startingDate), dateToString(new Date(updatedDate)));
        }
      } else if (startingDate && numberOfNights) {
        const updatedDate = dayjs(startingDate).add(numberOfNights, 'day').format(dateFormat);
        setEndDate(new Date(updatedDate));
        onChanging(dateToString(startingDate), dateToString(new Date(updatedDate)));
      }
      setDuration(numberOfNights);
      if (onDurationChange) onDurationChange(numberOfNights);
    },
    [finalDate, startingDate, onChanging, dateFormat]
  );

  const noWeekDayConstraints = (date: Date): boolean => {
    if (!!weekDayConstraints && weekDayConstraints.length > 0) {
      const day = date.getDay();
      return !weekDayConstraints.find(constraint => {
        return (
          (!constraint.fromDate ||
            !constraint.toDate ||
            (!!constraint.fromDate &&
              !!constraint.toDate &&
              dayjs(date).isAfter(dayjs(constraint.fromDate).subtract(1, 'day')) &&
              dayjs(date).isBefore(dayjs(constraint.toDate).add(1, 'day')))) &&
          constraint.weekDays?.includes(day)
        );
      });
    } else {
      return true;
    }
  };

  const inRange = (date: Date): boolean => {
    if (!maxDateRangeSpan === undefined && !minDateRangeSpan === undefined) {
      return true;
    }
    const localStartDate = useLocalSelectedDatesForInRangeCheck ? startingDate : startDate;
    if (maxDateRangeSpan !== undefined) {
      const start = dayjs(localStartDate)
        .subtract(1, 'day')
        .add(minDateRangeSpan ?? 0, 'day');

      let end = dayjs(localStartDate).add(maxDateRangeSpan + 1, 'day');

      for (const seasonEndDate of seasonsEndDates) {
        const currentEndDate = dayjs(seasonEndDate);
        if (currentEndDate.isAfter(start) && currentEndDate.isBefore(end)) {
          end = currentEndDate.add(1, 'day');
        }
      }

      const current = dayjs(date);

      // check if date is in range + allow wider selection when both selection are there
      return (current.isAfter(start) && current.isBefore(end)) || finalDate != null;
    }
    if (minDateRangeSpan !== undefined) {
      const start = dayjs(localStartDate).subtract(1, 'day');
      const end = dayjs(localStartDate).subtract(1, 'day').add(minDateRangeSpan, 'day');

      const current = dayjs(date);

      // check if date is in range + allow wider selection when both selection are there
      return !current.isAfter(start) || current.isAfter(end) || finalDate != null;
    }
    return true;
  };

  const inWeekdayConstraintsRange = (date: Date): boolean => {
    if (!weekDayConstraints || (!!weekDayConstraints && weekDayConstraints.length === 0)) {
      return true;
    }
    return !weekDayConstraints
      .reduce(
        (
          total: Array<{ fromDate: string; toDate: string }>,
          current: WeekDayConstraints,
          idx: number
        ) => {
          return idx < weekDayConstraints.length - 1
            ? [
                ...total,
                {
                  fromDate: current.toDate || '',
                  toDate: weekDayConstraints[idx + 1].fromDate || ''
                }
              ]
            : [...total];
        },
        [] as Array<{ fromDate: string; toDate: string }>
      )
      .some(
        item =>
          dayjs(date).isAfter(dayjs(item.fromDate)) && dayjs(date).isBefore(dayjs(item.toDate))
      );
  };

  const getFilterDate = (date: Date): boolean => {
    return inRange(date) && noWeekDayConstraints(date) && inWeekdayConstraintsRange(date);
  };

  const applyMinRange = (): Date[] => {
    if (
      !minDateRangeSpan ||
      !closeOnSelect ||
      (!updateStateFromProps && !startingDate && clearSelectionsOnOpen)
    )
      return [];
    const result: Date[] = [];
    for (let i = 0; i < minDateRangeSpan - 1; i++) {
      result.push(
        dayjs(startDate)
          .add(i + 1, 'day')
          .toDate()
      );
    }
    return result;
  };

  const windowSize = useWindowSize();
  let isSmall = false;
  if (windowSize.width <= 600) {
    isSmall = true;
  }

  const handleCalendarOpen = (): void => {
    if (clearSelectionsOnOpen) {
      setPreviousStartDate(startingDate);
      setPreviousEndDate(finalDate);
      setStartDate(undefined);
      setEndDate(undefined);
    }
    setIsMenuOpen(true);
    if (!!onCalendarOpen) {
      onCalendarOpen();
    }
    if (updateStateFromProps) {
      setIsCalendarOpen(true);
    }
  };

  const handleCalendarClose = (): void => {
    if (!!onCalendarClose) {
      setIsMenuOpen(false);
      onCalendarClose();
    } else if (!!onCalendarCloseDated && startingDate && finalDate) {
      if (onCalendarCloseDated) {
        onCalendarCloseDated(dateToString(startingDate), dateToString(finalDate));
      }
    } else if (
      fillWithLastDateRangeWhenNoSelection &&
      previousStartingDate &&
      !startingDate &&
      previousFinalDate &&
      !finalDate
    ) {
      setStartDate(previousStartingDate);
      setEndDate(previousFinalDate);
    } else if (finalDate) {
      setIsMenuOpen(false);
      if (updateStateFromProps) {
        setIsCalendarOpen(false);
      }
    } else {
      handleDurationChange(duration);
    }
  };

  const togglePopover = (): void => {
    if (!dateRangeInputRef) {
      setPopoverOpen(value => !value);
      if (!!onPopoverToggle) {
        onPopoverToggle();
      }
    }
  };

  function updatePackageAvailability(onMonthChangeDate: Date) {
    const visibleMonths = isSmall ? 1 : monthsShown || 2;
    const availabilitySearchStartDate = dayjs(onMonthChangeDate.toDateString()).startOf('month');
    const availabilitySearchEndDate = availabilitySearchStartDate
      .add(visibleMonths - 1, 'month')
      .endOf('month');
    loadPackageAvailability(
      availabilitySearchStartDate.format('YYYY-MM-DD'),
      availabilitySearchEndDate.diff(availabilitySearchStartDate, 'day', false) + 1
    );
  }

  function loadPackageAvailability(searchStartDate: string, searchTolerancePlus: number) {
    if (withColorCodedAvailability) {
      const request = getPackageSearchParams(searchStartDate, searchTolerancePlus);

      if (request) {
        setIsLoading(true);
        api
          .getPackageAvailability(request)
          .then(response => {
            setAvailabilityPackage(response.packages[0] || []);
          })
          .catch(error => {
            logger.log(error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }

  function getPackageSearchParams(
    searchStartDate: string,
    searchTolerancePlus: number
  ): ApiGetPackagesRequest | null {
    const encodedItem = query.param(ITEM);
    const productId = searchParams?.productId;
    if (encodedItem || productId) {
      const salesChannel = searchParams?.salesChannelCode
        ? searchParams.salesChannelCode
        : query.param(SALES_CHANNEL_CODE);
      const occupancy = query.param(OCCUPANCY);

      const packageCode = searchParams?.code ? searchParams.code : query.param(PACKAGE_CODE);
      const item = searchParams?.productId
        ? decodeObjectBase64<ServiceKey>(ServiceKey, searchParams.productId || '')
        : decodeObjectBase64<ServiceKey>(ServiceKey, encodedItem || '');
      const mainServiceCodes = item.serviceCode ? item.serviceCode : '';
      const mainServiceTypeCodes = item.serviceTypeCode ? item.serviceTypeCode : [];

      return ApiGetPackagesRequestFromJSON({
        destination: '',
        startDate: searchStartDate,
        classCode: '',
        calculatePrices: false,
        subType: ApiItemType.PACKAGE,
        salesChannel: salesChannel,
        mainServiceCodes: mainServiceCodes ? [mainServiceCodes] : [],
        serviceUnitCodes: selectedRoomServiceUnitTypeCode ? [selectedRoomServiceUnitTypeCode] : [],
        mainServiceTypeCodes: [mainServiceTypeCodes],
        toleranceMinus: 0,
        tolerancePlus: searchTolerancePlus,
        packageCode: packageCode,
        roomContainer: searchParams?.occupancy
          ? searchParams.occupancy
          : occupancy
          ? JSON.parse(occupancy)
          : []
      });
    }

    return null;
  }

  let currentMonthsShown = isSmall ? 1 : monthsShown || 2;
  if (typeof minMonthShown === 'number' && minMonthShown > currentMonthsShown) {
    currentMonthsShown = minMonthShown;
  }

  const renderDatePicker = (
    renderInline?: boolean,
    useCustomDateFormat?: boolean
  ): ReactElement => (
    <DatePickerComponent
      ref={datePickerRef}
      inline={renderInline}
      dataTestId={dataTestId}
      date={startingDate}
      dateFormat={normalizedDateFormatForComponent}
      startDate={startingDate}
      minDate={minDate}
      maxDate={maxDate}
      onChange={handleChange}
      onCalendarOpen={handleCalendarOpen}
      onCalendarClose={handleCalendarClose}
      onMonthChange={onMonthChange}
      onYearChange={onYearChange}
      endDate={finalDate}
      selectsRange={selectsRange ? true : undefined}
      filterDate={
        (!ignoreStartDateForFilter && startingDate) || ignoreStartDateForFilter
          ? getFilterDate
          : undefined
      }
      excludeDates={getExcludedDates()}
      monthsShown={currentMonthsShown}
      shouldCloseOnSelect={closeOnSelect}
      showPreviousMonths={false}
      id={id}
      noDatePickerOverflow={noDatePickerOverflow}
      disabled={disabled}
      selectsDisabledDaysInRange={selectsDisabledDaysInRange}
      handleDurationChange={handleDurationChange}
      usePlaceholderAsOption={usePlaceholderAsOption}
      durationNumber={duration}
      withDuration={withDuration}
      customInput={
        (CustomInput || (
          <DateRangeDisplay
            customDateFormat={customDateFormat}
            firstDate={startingDate ? dateToString(startingDate) : ''}
            secondDate={finalDate ? dateToString(finalDate) : ''}
            displayIcon={theme.useCalenderIcon}
            fromTranslation={t(Keys.from)}
            untilTranslation={t(Keys.to)}
            dataTestId={dataTestId}
            displayDuration={withDuration}
            duration={durationToString()}
            customDivider={customDivider}
          />
        )) as ReactElement
      }
      focusSelectedMonth={focusSelectedMonth}
      useAdvancedFeatures={useAdvancedFeatures}
      availabilityPackage={availabilityPackage}
      updatePackageAvailability={updatePackageAvailability}
      showInModal={showDateSelectionInModal}
      modalLabel={modalLabel}
      customDivider={customDivider}
      duration={durationToString()}
      customDateFormat={useCustomDateFormat}
      renderDayContents={
        withColorCodedAvailability || highlightDatesInformation
          ? renderDayContents(
              config,
              availabilityPackage,
              getIncludedDatesFromSeasons(),
              highlightDatesInformation,
              withColorCodedAvailability
            )
          : undefined
      }
      includeDates={getIncludedDatesFromSeasons()}
      showPopperArrow={showPopperArrow}
      withHeader={withHeader}
      popperPlacement={popperPlacement}
    />
  );

  return (
    <div
      className={`iso__date-range${!!startDate && !!endDate ? ' input--filled' : ''}${
        disabled ? ' iso__date-range--disabled' : ''
      }${isMenuOpen ? ' iso__date-range--open' : ''}`}
      ref={ref || containerRef}
    >
      {!!popoverTargetRef ? (
        <DateRangeInputPopover
          ref={ref}
          containerRef={containerRef}
          popoverRef={popoverRef}
          popoverTargetRef={popoverTargetRef}
          popoverOpen={popoverOpen}
          togglePopover={togglePopover}
          externalPopoverRef={externalPopoverRef}
          isLoading={isLoading}
          submit={submit}
          CustomHeader={CustomHeader}
          withColorCodedAvailability={withColorCodedAvailability}
          onSubmit={onSubmit}
        >
          {renderDatePicker(true, customDateFormat)}
        </DateRangeInputPopover>
      ) : (
        renderDatePicker()
      )}
    </div>
  );
});

export default DateRangeInput;
